import { defineStore } from "pinia";

import router from "@/router/routerConfig";
import api from "@/stores/api";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null,
    menu: [],
    isLoading: false,
    isInitialized: false,
  }),
  getters: {
    isAuthenticated: (state) => !!state.user,
    userName: (state) => state.user?.name || "",
    userEmail: (state) => state.user?.email || "",
    userId: (state) =>
      state.user?.id ||
      state.user?.user_id ||
      state.user?.username ||
      state.user?.email ||
      null,
    isAdmin: (state) => {
      const roles = state.user?.roles || [];
      return roles.includes("ADMINISTRADOR");
    },
    userRoles: (state) => state.user?.roles || [],
  },
  actions: {
    async initializeAuth() {
      if (this.isInitialized) return;

      try {
        await this.fetchUser();
        this.isInitialized = true;
      } catch (error) {
        console.warn("[Auth] Initialization failed:", error.message);
        this.isInitialized = true;
      }
    },

    async fetchUser() {
      try {
        this.isLoading = true;
        const response = await api().get("/auth/me");
        const newUser = response.data;

        if (this.user && this.user.id !== newUser.id) {
          try {
            const { useIntegratedReviewStore } = await import(
              "@/stores/integrated-review.store.js"
            );
            const integratedReviewStore = useIntegratedReviewStore();
            integratedReviewStore.clearUserCache();
          } catch (cacheError) {
            console.warn(
              "[Auth] Failed to clear review cache on user change:",
              cacheError.message
            );
          }
        }

        this.user = newUser;
      } catch (error) {
        this.user = null;
        this.menu = [];

        console.error("[Auth] fetchUser failed:", {
          hasResponse: !!error.response,
          status: error.response?.status,
          detail: error.response?.data?.detail,
          hasRequest: !!error.request,
          message: error.message,
          cookies: document.cookie,
        });

        if (error.response) {
          console.warn("[Auth] Falha na validação de sessão:", {
            status: error.response.status,
            message: error.response.data?.detail || "Erro desconhecido",
          });
        } else if (error.request) {
          console.error("[Auth] Sem resposta do servidor:", error.message);
        } else {
          console.error("[Auth] Erro na configuração:", error.message);
        }
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    clearSession() {
      this.user = null;
      this.menu = [];
      this.isInitialized = false;
      try {
        sessionStorage.clear();
      } catch (e) {
        console.warn("[Auth] Não foi possível limpar sessionStorage:", e);
      }
    },

    async logout() {
      try {
        await api().post("/auth/logout", null, {
          params: { skip_consent: true },
        });
        
        try {
          const { useIntegratedReviewStore } = await import(
            "@/stores/integrated-review.store.js"
          );
          const integratedReviewStore = useIntegratedReviewStore();
          integratedReviewStore.clearUserCache();
        } catch (cacheError) {
          console.warn(
            "[Auth] Failed to clear review cache:",
            cacheError.message
          );
        }

        this.clearSession();
        window.location.href = "/login";
      } catch (error) {
        console.error("[Auth] Erro durante logout:", {
          message: error.message,
          status: error.response?.status,
        });
        try {
          const { useIntegratedReviewStore } = await import(
            "@/stores/integrated-review.store.js"
          );
          const integratedReviewStore = useIntegratedReviewStore();
          integratedReviewStore.clearUserCache();
        } catch (cacheError) {
          console.warn(
            "[Auth] Failed to clear review cache on error:",
            cacheError.message
          );
        }

        this.clearSession();
        router.push({ name: "Login" });
      }
    },
  },
  persist: {
    storage: sessionStorage,
    paths: ["user"],
  },
});
